import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Paper,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Switch,
  TextField,
} from "@mui/material";
import TermsEditor, { readyItems, useTermCatalog } from "./TermsEditor";
import useCaseList from "../../hooks/useCaseList.js";
import AgGridPaginatedTable from "../../components/AgGridPaginatedTable";
import { negotiationListTable, negotiationListNarrowHide } from "../../config/grid-column-defs";
import useNarrowColumns from "../../hooks/useNarrowColumns.js";
import { useDispatch, useSelector } from "react-redux";
import { caseActions } from "../../config/action-config";
import {
  addNewNegotiation,
  getCaseNegotiations,
} from "../../api/caseNegotiations";
import useSnackbar from "../../hooks/useSnackbar.js";
import ConditionalRenderer from "../../components/ConditionalRenderer.jsx";
import useErrorHandler from "../../hooks/useErrorHandler.js";
import {
  RESPONSE_HOURS_DEFAULT,
  RESPONSE_HOURS_OPTIONS,
  responseHoursLabel,
} from "../../config/negotiation-utils";

export default function CaseNegotiations() {
  // return <></>
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const columns = useNarrowColumns(negotiationListTable, negotiationListNarrowHide);
  const { data: user } = useSelector((state) => state.user);
  const errorHandler = useErrorHandler();
  const [negotiationTableRefreshCount, setNegotiationTableRefreshCount] =
    useState(0);
  function refreshTable() {
    setNegotiationTableRefreshCount((prev) => prev + 1);
  }

  const { caseList, setSelectedCaseId } = useCaseList();
  setSelectedCaseId(params.caseId);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [isPrimary, setIsPrimary] = useState(false);

  // Switching case here keeps you on the list instead of sending you back to pick one again.
  function onCaseChange({ target }) {
    setSelectedCaseId(target.value);
    dispatch({ type: caseActions.SELECT, payload: { id: target.value } });
    setIsPrimary(false); // re-evaluated for the new case when its rows load
    navigate(target.value ? `/negotiation/${target.value}` : "/negotiation");
  }

  async function getNegotiationTableData(limit, offset) {
    try {
      const { data } = await getCaseNegotiations(user.token, params.caseId, {
        limit,
        offset,
      });
      setIsPrimary(data.primary);
      return { rows: data.data, total: data.total, error: false };
    } catch (error) {
      errorHandler(error);
      return { error: true };
    } finally {
      // setLoading(false);
    }
  }

  const snackbar = useSnackbar();
  async function onAddDone(error = false) {
    if (error) {
      snackbar.showError();
    } else {
      snackbar.showSuccess("Negotiation Added.");
      refreshTable();
    }
  }

  return (
    <>
      <Paper className="odr-card">
        <Box p={2} className="flex flex-wrap items-center justify-between gap-3">
          <FormControl size="small" className="w-full min-[600px]:w-[360px]">
            <InputLabel id="negotiation-case-label">Case</InputLabel>
            <Select
              labelId="negotiation-case-label"
              label="Case"
              value={caseList.some((c) => String(c.id) === String(params.caseId)) ? String(params.caseId) : ""}
              onChange={onCaseChange}
            >
              {caseList.map((c) => (
                <MenuItem key={c.id} value={String(c.id)}>
                  {c.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <ConditionalRenderer show={/true/.test(isPrimary)}>
            <Button variant="contained" size="small" color="primary" onClick={() => setDialogOpen(true)}>
              Add New
            </Button>
          </ConditionalRenderer>
        </Box>

        <Box className="odr-main-table" pt={1}>
          <AgGridPaginatedTable
            key={params.caseId}
            columns={columns}
            getTableRows={getNegotiationTableData}
            refreshCounter={negotiationTableRefreshCount}
          />
        </Box>
      </Paper>
      <AddNewDialog
        open={dialogOpen}
        setOpen={setDialogOpen}
        onAddDone={onAddDone}
        token={user.token}
        caseId={params.caseId}
      />
    </>
  );
}

const emptyData = {
  title: "",
  description: "",
  amount: "",
  response_hours: RESPONSE_HOURS_DEFAULT,
  mode: "OPEN",
  monetary: true,
  terms: "",
  max_rounds: "",
};
const MAX_ROUND_OPTIONS = [3, 5, 10];
const MODE_HELP = {
  OPEN: "Each side makes visible offers and counter-offers until one accepts.",
  BLIND:
    "Each side submits a confidential figure. If the defendant's figure reaches the plaintiff's, you settle at the midpoint; otherwise the next round opens (3 rounds).",
};
export function AddNewDialog({ open = true, setOpen, onAddDone, token, caseId }) {
  const [data, setData] = useState(emptyData);
  const [termItems, setTermItems] = useState([]);
  const { catalog, maxItems } = useTermCatalog(caseId);
  const errorHandler = useErrorHandler();
  const snackbar = useSnackbar();
  const [amountError, setAmountError] = useState("");
  const handleInputChange = ({ target }) => {
    const { value, name } = target;
    setData((prev) => ({ ...prev, [name]: value }));
    if (name === "amount") setAmountError("");
  };

  // a negotiation about terms only has no money, so it can't use confidential bidding
  const onMonetaryChange = ({ target }) =>
    setData((prev) => ({ ...prev, monetary: target.checked, ...(target.checked ? {} : { mode: "OPEN", amount: "" }) }));

  const handleClose = () => {
    setAmountError("");
    setOpen(false);
  };

  async function handleFormSubmit(e) {
    e.preventDefault();
    const amount = Number(data.amount);
    if (!data.title.trim() || !data.description.trim()) {
      snackbar.showError("Title and description are required.");
      return;
    }
    const ready = readyItems(termItems, catalog);
    if (ready.error) {
      snackbar.showError(ready.error);
      return;
    }
    if (!data.monetary && !ready.items.length) {
      snackbar.showError("Add at least one term, or include a money amount.");
      return;
    }
    const blind = data.monetary && data.mode === "BLIND";
    if (data.monetary && !blind && !(amount > 0)) {
      setAmountError("Enter an opening offer greater than $0.");
      return;
    }
    const body = {
      title: data.title,
      description: data.description,
      response_hours: data.response_hours,
    };
    if (!data.monetary) body.monetary = false;
    if (blind) body.mode = "BLIND";
    else {
      if (data.monetary) body.amount = amount;
      if (data.max_rounds) body.max_rounds = Number(data.max_rounds);
    }
    if (ready.items.length) body.terms_items = ready.items;
    if (data.terms.trim()) body.terms = data.terms.trim();
    try {
      await addNewNegotiation(token, caseId, body);
      setData(emptyData);
      setTermItems([]);
      setOpen(false);
      onAddDone();
    } catch (error) {
      // keep the dialog open so the user can correct the input
      errorHandler(error);
    }
  }

  return (
    <Dialog
      open={open}
      //onClose={handleClose}
      onClose={() => {       
      }}
      aria-describedby="alert-dialog-slide-description"
    >
      <DialogTitle className="heading-title">Create</DialogTitle>
      <DialogContent>
        <Box component="form" pt={1} onSubmit={handleFormSubmit}>
          <Grid container spacing={1}>
            <Grid item md={6} xl={6}>
              <TextField
                name="title"
                size="small"
                fullWidth
                required
                onChange={handleInputChange}
                value={data.title}
                label="Title"
              />
            </Grid>
            {data.monetary && data.mode === "OPEN" ? (
              <Grid item md={6} xl={6}>
                <TextField
                  type="number"
                  name="amount"
                  size="small"
                  fullWidth
                  required
                  onChange={handleInputChange}
                  value={data.amount}
                  inputProps={{ min: 0, step: "any" }}
                  error={Boolean(amountError)}
                  helperText={amountError}
                  label="Amount($)"
                />
              </Grid>
            ) : null}
            <Grid item md={12} xl={12}>
              <TextField
                name="description"
                size="small"
                fullWidth
                required
                onChange={handleInputChange}
                value={data.description}
                label="Description"
              />
            </Grid>
            <Grid item md={12} xl={12}>
              <FormControlLabel
                control={<Switch checked={data.monetary} onChange={onMonetaryChange} />}
                label="This negotiation includes a money amount"
              />
              {!data.monetary ? (
                <FormHelperText>
                  Terms only: the parties negotiate non-monetary terms (for example a move-out date or an apology). Add at least one below.
                </FormHelperText>
              ) : null}
            </Grid>
            <Grid item md={12} xl={12}>
              <FormLabel className="!text-[13px]">Terms{data.monetary ? " (optional)" : ""}</FormLabel>
              <Box className="mt-1">
                <TermsEditor catalog={catalog} maxItems={maxItems} items={termItems} onChange={setTermItems} />
              </Box>
            </Grid>
            <Grid item md={12} xl={12}>
              <TextField
                name="terms"
                size="small"
                fullWidth
                multiline
                minRows={2}
                onChange={handleInputChange}
                value={data.terms}
                inputProps={{ maxLength: 2000 }}
                label="Additional terms (optional)"
                helperText="Anything not covered by the terms above, in your own words."
              />
            </Grid>
            {data.monetary ? (
            <Grid item md={12} xl={12}>
              <FormControl>
                <FormLabel id="bidding-method-label" className="!text-[13px]">
                  Bidding method
                </FormLabel>
                <RadioGroup
                  row
                  aria-labelledby="bidding-method-label"
                  name="mode"
                  value={data.mode}
                  onChange={handleInputChange}
                >
                  <FormControlLabel value="OPEN" control={<Radio size="small" />} label="Open offers" />
                  <FormControlLabel value="BLIND" control={<Radio size="small" />} label="Confidential bidding" />
                </RadioGroup>
                <FormHelperText>{MODE_HELP[data.mode]}</FormHelperText>
              </FormControl>
            </Grid>
            ) : null}
            {data.mode === "OPEN" ? (
              <Grid item md={12} xl={12}>
                <TextField
                  select
                  name="max_rounds"
                  size="small"
                  fullWidth
                  onChange={handleInputChange}
                  value={data.max_rounds}
                  label="Maximum rounds"
                  helperText="The opening offer counts as round 1."
                >
                  <MenuItem value="">No limit</MenuItem>
                  {MAX_ROUND_OPTIONS.map((r) => (
                    <MenuItem key={r} value={r}>
                      {r}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            ) : null}
            <Grid item md={12} xl={12}>
              <TextField
                select
                name="response_hours"
                size="small"
                fullWidth
                onChange={handleInputChange}
                value={data.response_hours}
                label="Response time"
                helperText={
                  data.mode === "BLIND"
                    ? "How long each side has to submit a confidential figure in every round."
                    : "How long the other party has to respond before the offer expires."
                }
              >
                {RESPONSE_HOURS_OPTIONS.map((h) => (
                  <MenuItem key={h} value={h}>
                    {responseHoursLabel(h)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          size="large"
          onClick={handleClose}
          className="black-border-button"
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="large"
          className="blue-button"
          onClick={handleFormSubmit}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}
